import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface Questionnaire {
  id: string;
  version: string | null;
  category: string | null;
  meta: any;
  created_at: string;
  archived: boolean;
}

interface Question {
  id: number;
  question_text: string;
  domain: string;
  evidence_types: string[];
}

interface AdminQuestionnairesTableProps {
  onLogAction: (action: string, tableName?: string, recordId?: string, oldValues?: any, newValues?: any, reason?: string) => Promise<void>;
}

export const AdminQuestionnairesTable = ({ onLogAction }: AdminQuestionnairesTableProps) => {
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selectedTab, setSelectedTab] = useState<'questionnaires' | 'questions'>('questionnaires');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    action: () => void;
  }>({ open: false, title: "", description: "", action: () => {} });

  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Use admin RPC for questionnaires
      const { data: questionnairesData, error: questionnairesError } = await supabase.rpc('admin_list_questionnaires');
      if (questionnairesError) throw questionnairesError;

      // Load questions - try admin RPC first, fallback to direct table query
      const runQuestions = async () => {
        const rpcRes = await (supabase as any).rpc("admin_list_questions");
        if (!rpcRes.error && rpcRes.data) return rpcRes;

        console.warn("[AdminQuestionnaires] rpc admin_list_questions failed, falling back", rpcRes.error);
        
        const tableRes = await supabase
          .from('questions')
          .select('id, question_text, domain, evidence_types')
          .order('id');
        return tableRes;
      };

      const { data: questionsData, error: questionsError } = await runQuestions();
      if (questionsError) throw questionsError;

      // Sort questions client-side by id
      const sortedQuestions = (questionsData || []).sort((a: any, b: any) => a.id - b.id);
      
      console.log("[AdminQuestionnaires] questions loaded:", { total: sortedQuestions.length });

      setQuestionnaires(questionnairesData || []);
      setQuestions(sortedQuestions);
    } catch (error) {
      console.error('Error loading questionnaires data:', error);
      toast({
        title: "Error",
        description: "Failed to load questionnaires data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const publishQuestionnaire = async (questionnaireId: string, reason: string) => {
    try {
      // In a real implementation, you would update the questionnaire status
      // For now, we'll just log the action
      await onLogAction(
        'PUBLISH_QUESTIONNAIRE',
        'questionnaires',
        questionnaireId,
        null,
        { status: 'published' },
        reason
      );

      toast({ title: "Success", description: "Questionnaire published successfully" });
    } catch (error) {
      console.error('Error publishing questionnaire:', error);
      toast({
        title: "Error",
        description: "Failed to publish questionnaire",
        variant: "destructive"
      });
    }
  };

  // Extract title from meta JSON
  const getQuestionnaireTitle = (meta: any): string | null => {
    if (!meta) return null;
    if (typeof meta === 'string') {
      try {
        const parsed = JSON.parse(meta);
        return parsed?.title || null;
      } catch {
        return null;
      }
    }
    return meta?.title || null;
  };

  const filteredQuestionnaires = questionnaires.filter(q => {
    if (q.archived) return false;
    const title = getQuestionnaireTitle(q.meta);
    return !filter || 
      q.id?.toLowerCase().includes(filter.toLowerCase()) ||
      q.category?.toLowerCase().includes(filter.toLowerCase()) ||
      title?.toLowerCase().includes(filter.toLowerCase());
  });

  const filteredQuestions = questions.filter(q => {
    return !filter || 
      q.question_text?.toLowerCase().includes(filter.toLowerCase()) ||
      q.domain?.toLowerCase().includes(filter.toLowerCase());
  });

  if (loading) {
    return <div className="flex justify-center p-8">Loading questionnaires...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-4 items-center">
        <div className="flex gap-2">
          <Button 
            variant={selectedTab === 'questionnaires' ? 'default' : 'outline'}
            onClick={() => setSelectedTab('questionnaires')}
          >
            Questionnaires
          </Button>
          <Button 
            variant={selectedTab === 'questions' ? 'default' : 'outline'}
            onClick={() => setSelectedTab('questions')}
          >
            Questions Bank
          </Button>
        </div>
        <Input
          placeholder={`Search ${selectedTab}...`}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {selectedTab === 'questionnaires' && (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredQuestionnaires.map((questionnaire) => {
                const title = getQuestionnaireTitle(questionnaire.meta);
                return (
                  <TableRow key={questionnaire.id}>
                    <TableCell>
                      <span className="font-medium">{title || 'Untitled'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm text-muted-foreground">{questionnaire.id}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{questionnaire.category || 'N/A'}</Badge>
                    </TableCell>
                    <TableCell>{questionnaire.version}</TableCell>
                    <TableCell>
                      {questionnaire.archived ? (
                        <Badge variant="secondary" className="text-muted-foreground">Archived</Badge>
                      ) : (
                        <Badge variant="default">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {format(new Date(questionnaire.created_at), 'PPp')}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmDialog({
                          open: true,
                          title: "Publish Questionnaire",
                          description: `Are you sure you want to publish questionnaire ${questionnaire.id}?`,
                          action: () => publishQuestionnaire(questionnaire.id, '')
                        })}
                      >
                        Publish
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {selectedTab === 'questions' && (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Domain</TableHead>
                <TableHead>Question Text</TableHead>
                <TableHead>Evidence Types</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredQuestions.map((question) => (
                <TableRow key={question.id}>
                  <TableCell>
                    <span className="font-mono text-sm">{question.id}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{question.domain}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-md truncate" title={question.question_text}>
                      {question.question_text}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {question.evidence_types.map((type, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {type}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmationDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, open }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={(reason) => {
          confirmDialog.action();
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }}
      />
    </div>
  );
};